import React, { useMemo } from 'react';
import {
  Box,
  Paper,
  Typography,
  Alert,
  Divider,
  Grid,
  Stack,
} from '@mui/material';
import { Download as DownloadIcon } from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import { AdvancedExpressionBuilder } from '@/shared/components/AdvancedExpressionBuilder';
import { 
  SingleSelectField, 
  MultiSelectField, 
  FormFieldWrapper
} from '@/components';
import type { SelectOption } from '@/components/SingleSelectField';
import { useDataExport } from '../hooks/useDataExport';
import OrganizationSelect from '../../../../OrganizationNotifications/components/OrganizationSelect';
import { NATIVE_FIELDS, SORT_ATTRIBUTES } from '../constants/dataExport.constants';

export interface CustomField {
  id: string;
  identifier: string;
  label: string;
  fieldType: string;
  entity?: string;
}

const SORT_DIRECTIONS: SelectOption[] = [
  { value: 'ASC', label: 'Crescente' },
  { value: 'DESC', label: 'Decrescente' }
];

const DataExportForm: React.FC = () => {
  const {
    organizations,
    workflows,
    customFields,
    selectedOrganizationId,
    selectedWorkflowId,
    selectedNativeFields,
    selectedCustomFields,
    sortAttribute,
    sortDirection,
    conditions,
    loadingState,
    error,
    success,

    handleOrganizationChange,
    handleWorkflowChange,
    handleNativeFieldsChange,
    handleCustomFieldsChange,
    handleSortChange,
    handleConditionsChange,
    handleSubmit,

    fetchFieldCatalog,
    fetchResourceValues,
  } = useDataExport();

  const handleExportClick = async () => {
    try {
      await handleSubmit();
    } catch (err) {
      // Error is already handled in the hook
    }
  };

  const isFormValid = selectedWorkflowId &&
    (selectedNativeFields.length > 0 || selectedCustomFields.length > 0);

  // Memoized option mappings to avoid recalculation on every render
  const nativeFieldOptions: SelectOption[] = useMemo(() =>
    NATIVE_FIELDS.map((field: { identifier: string; label: string }) => ({
      value: field.identifier,
      label: field.label
    })), []
  );

  const customFieldOptions: SelectOption[] = useMemo(() =>
    customFields.map((field: any) => ({
      value: field.identifier,
      label: field.label
    })), [customFields]
  );

  // Memoized selected values to avoid recalculation on every render
  const selectedWorkflow = useMemo(() =>
    workflows.find(workflow => workflow.id === selectedWorkflowId), 
    [workflows, selectedWorkflowId]
  );

  const selectedSortAttribute = useMemo(() =>
    SORT_ATTRIBUTES.find(attr => attr.value === sortAttribute) || null,
    [sortAttribute]
  );

  const selectedSortDirection = useMemo(() =>
    SORT_DIRECTIONS.find(dir => dir.value === sortDirection) || null,
    [sortDirection]
  );

  const selectedNativeFieldOptions = useMemo(() =>
    nativeFieldOptions.filter(field => selectedNativeFields.includes(field.value)),
    [nativeFieldOptions, selectedNativeFields]
  );

  const selectedCustomFieldOptions = useMemo(() =>
    customFieldOptions.filter(field => selectedCustomFields.includes(field.value)),
    [customFieldOptions, selectedCustomFields]
  );

  // Centralized logic for disabling workflow select
  const isWorkflowSelectDisabled = !selectedOrganizationId || loadingState.isLoadingWorkflows;

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', p: 3 }}>
      <Paper elevation={2} sx={{ p: 4 }}>
        <Typography variant="h4" gutterBottom sx={{ mb: 4 }}>
          Exportação de Dados
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 3 }}>
            {success}
          </Alert>
        )}

        <Stack spacing={4}>
          <FormFieldWrapper title="1. Contexto">
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <OrganizationSelect
                  organizations={organizations}
                  selectedOrganization={selectedOrganizationId}
                  onOrganizationChange={handleOrganizationChange}
                  isLoading={loadingState.isLoadingOrganizations}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <SingleSelectField
                  options={workflows.map(w => ({ value: w.id, label: w.name }))}
                  value={selectedWorkflow ? { value: selectedWorkflow.id, label: selectedWorkflow.name } : null}
                  onChange={(newValue: SelectOption | null) =>
                    newValue && handleWorkflowChange(newValue.value)
                  }
                  label="Workflow"
                  placeholder="Selecione um workflow..."
                  loading={loadingState.isLoadingWorkflows}
                  loadingText="Carregando workflows..."
                  noOptionsText={loadingState.isLoadingWorkflows
                    ? "Carregando..."
                    : "Nenhum workflow encontrado"
                  }
                  disabled={isWorkflowSelectDisabled}
                />
              </Grid>
            </Grid>
          </FormFieldWrapper>

          <FormFieldWrapper title="2. Campos para Exportação">
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <MultiSelectField
                  options={nativeFieldOptions}
                  value={selectedNativeFieldOptions}
                  onChange={(newValue: SelectOption[]) => {
                    const selectedValues = newValue.map((item: SelectOption) => item.value);
                    handleNativeFieldsChange(selectedValues);
                  }}
                  label="Campos Nativos"
                  placeholder="Selecione campos..."
                  chipColor="primary"
                  enableSelectAll={true}
                  selectAllText="Selecionar todos"
                  maxChips={10}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <MultiSelectField
                  options={customFieldOptions}
                  value={selectedCustomFieldOptions}
                  onChange={(newValue: SelectOption[]) => {
                    const selectedValues = newValue.map((item: SelectOption) => item.value);
                    handleCustomFieldsChange(selectedValues);
                  }}
                  label={loadingState.isLoadingCustomFields
                    ? "Carregando campos customizados..."
                    : "Campos Customizados"
                  }
                  placeholder="Selecione campos..."
                  loading={loadingState.isLoadingCustomFields}
                  disabled={loadingState.isLoadingCustomFields}
                  chipColor="secondary"
                  enableSelectAll={true}
                  selectAllText="Selecionar todos"
                  maxChips={10}
                />
              </Grid>
            </Grid>
          </FormFieldWrapper>

          <FormFieldWrapper title="3. Ordenação">
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <SingleSelectField
                  options={SORT_ATTRIBUTES}
                  value={selectedSortAttribute}
                  onChange={(newValue: SelectOption | null) =>
                    newValue && handleSortChange(newValue.value, sortDirection)
                  }
                  label="Atributo de Ordenação"
                  placeholder="Selecione um atributo..."
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <SingleSelectField
                  options={SORT_DIRECTIONS}
                  value={selectedSortDirection}
                  onChange={(newValue: SelectOption | null) =>
                    newValue && handleSortChange(sortAttribute, newValue.value as 'ASC' | 'DESC')
                  }
                  label="Direção"
                  placeholder="Selecione a direção..."
                />
              </Grid>
            </Grid>
          </FormFieldWrapper>

          <FormFieldWrapper
            title="4. Filtros Avançados"
            description="Use o construtor de expressões abaixo para criar filtros complexos nos dados exportados."
          >
            <AdvancedExpressionBuilder
              initialExpressions={conditions || undefined}
              onChange={handleConditionsChange}
              fetchFieldCatalog={fetchFieldCatalog}
              fetchResourceValues={fetchResourceValues}
              isLoading={loadingState.isFetchingFields}
            />
          </FormFieldWrapper>

          <Box>
            <Divider sx={{ my: 3 }} />
            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
              <LoadingButton
                variant="contained"
                size="large"
                startIcon={<DownloadIcon />}
                onClick={handleExportClick}
                disabled={!isFormValid}
                loading={loadingState.isExporting}
                sx={{ minWidth: 200 }}
              >
                {loadingState.isExporting ? 'Exportando...' : 'Exportar Dados'}
              </LoadingButton>
            </Box>
          </Box>
        </Stack>
      </Paper>
    </Box>
  );
};

export default DataExportForm;
